import React, { useCallback, useMemo, useState } from 'react';
import { trimStart } from 'lodash';
import TextInput from '../../../components/TextInput';
import Button from '../../../buttons/DeprecatedButton/DeprecatedButton';
import TagModel from '@mm/state/model/TagModel';
import FilterableSelect from '../../../components/FilterableSelect/FilterableSelect';
import { ReactComponent as ClearIcon } from 'assets/icons/close.svg';
import { ReactComponent as AlertIcon } from 'assets/icons/alert-action.svg';

type Props = {
  tags: TagModel[];
  selectedTag: string | undefined;
  onSelect: (tagName: string) => void;
  onRemove: () => void;
};

export const OtnTagForm = (props: Props) => {
  const { onRemove, selectedTag, onSelect, tags } = props;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQeury] = useState('');
  const disabled = selectedTag !== undefined && selectedTag !== null;

  const onSearchQuery = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = trimStart(e.target.value);
      setSearchQeury(e.target.value);
    },
    [],
  );

  const onDropdownToggle = useCallback(
    () => setIsDropdownOpen(!isDropdownOpen),
    [isDropdownOpen],
  );

  const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    if (searchQuery) {
      onSelect(searchQuery.trim());
      setSearchQeury('');
    }
    onDropdownToggle();

    setSearchQeury('');
  };

  const onTagSelect = useCallback(
    (name: string, tag?: TagModel) => {
      if (disabled || tag?.name === undefined) {
        return;
      }

      onSelect(tag.name);

      setSearchQeury('');
      if (isDropdownOpen) {
        onDropdownToggle();
      }
    },
    [isDropdownOpen, disabled],
  );

  const suggestions = useMemo(() => {
    return [
      {
        title: '',
        suggestions: disabled ? tags.filter(e => e.name !== selectedTag) : tags,
      },
    ];
  }, [selectedTag, tags, disabled]);

  return (
    <div className="otn-widget__tag-form-wrapper">
      <form className="otn-widget__tag-form" onSubmit={onSubmitForm}>
        <label className="otn-widget__tag-form-label">
          <div className="otn-widget__tag-label-wrapper">
            <span>1-Time Notification Tag</span>
            <a
              className="otn-widget__otn-tag-tooltip"
              target="_blank"
              href="https://mobilemonkey.com/help/article/8xwzn7qmpe-1-time-notification-widget"
            >
              <i className="fa-question-circle far" />
            </a>
          </div>
          <TextInput
            className="otn-widget__tag-form-input"
            name="name"
            style={{ height: '50px', backgroundColor: 'white' }}
            disabled={disabled}
            value={searchQuery}
            autoComplete="false"
            readOnly
            placeholder="Select existing tag ot type to add new"
            onFocus={onDropdownToggle}
          />
          <Button
            type="submit"
            disabled={disabled}
            blue
            className="otn-widget__tag-form-button"
          >
            + Add
          </Button>
        </label>
        {isDropdownOpen && !disabled && (
          <FilterableSelect
            showSuggestOnly={true}
            defaultValue={searchQuery}
            className="otn-widget__tag-form-autocomplete"
            onBlur={onDropdownToggle}
            onInput={onSearchQuery}
            onChange={onTagSelect}
            name="filterable"
            searchPlaceholder="Tag name"
            suggestions={suggestions}
          />
        )}
      </form>
      <div className="otn-widget__tag-fb-guideline-wrapper">
        <AlertIcon className="otn-widget__tag-alert-icon" />
        Tag must meet
        <a
          className="otn-widget__tag-fb-guideline-link"
          target="_blank"
          href="https://mobilemonkey.com/help/article/lbfdv33b68-facebook-s-policy-guidelines-for-one-time-notifications-otn"
        >
          FB's Guidelines
        </a>
      </div>
      {disabled && (
        <div className="otn-widget__tag-list">
          <div className="otn-widget__tag">
            <div>{selectedTag}</div>
            <div className="otn-widget__tag-remove" onClick={onRemove}>
              <ClearIcon />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
